import type { Witness } from '../store/booksStore'
import type { AnchoredRow } from '../types/anchoredRowTypes'
import type { LedgerSafeWitnessStateSnapshot } from '../types/ledgerSafeLedgers'

type LegacySegmentationResult = Witness['segmentation_result']

const cloneJson = <T,>(value: T): T => JSON.parse(JSON.stringify(value))

export const cloneLegacySegmentationResult = (
  segmentationResult: LegacySegmentationResult,
): LegacySegmentationResult => (
  segmentationResult ? cloneJson(segmentationResult) : segmentationResult
)

export const buildLedgerSafeWitnessStateSnapshotFromWitness = (
  witness: Witness,
): LedgerSafeWitnessStateSnapshot => ({
  witnessId: witness.id,
  sigla: witness.sigla,
  processingStatus: witness.processing_status,
  segmentationResult: cloneLegacySegmentationResult(witness.segmentation_result),
})

export const buildLedgerSafeWitnessStateSnapshotsFromRows = (
  rows: AnchoredRow[],
  witnesses: Witness[],
): LedgerSafeWitnessStateSnapshot[] => {
  const mappedWitnessIds = new Set<string>()
  rows.forEach(row => {
    row.unitMappings.forEach(mapping => {
      if (mapping.witnessId && !mapping.excluded) {
        mappedWitnessIds.add(mapping.witnessId)
      }
    })
  })

  return witnesses
    .filter(witness => mappedWitnessIds.has(witness.id))
    .map(buildLedgerSafeWitnessStateSnapshotFromWitness)
}

export const rehydrateLedgerSafeWitnessStateSnapshotsFromWitnesses = (
  states: LedgerSafeWitnessStateSnapshot[],
  witnesses: Witness[],
): LedgerSafeWitnessStateSnapshot[] => {
  const witnessesById = new Map(witnesses.map(witness => [witness.id, witness]))
  return states.map(state => {
    if (state.segmentationResult !== undefined) {
      return {
        ...state,
        segmentationResult: cloneLegacySegmentationResult(state.segmentationResult),
      }
    }
    const witness = witnessesById.get(state.witnessId)
    return witness?.segmentation_result
      ? {
          ...state,
          segmentationResult: cloneLegacySegmentationResult(witness.segmentation_result),
        }
      : { ...state }
  })
}
